import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ChartLineIcon,
  ChatCircleIcon,
  FileTextIcon,
  PlayIcon,
  SquaresFourIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { Tabs, type TabItem } from "@libertify/ui";
import { FeatureFlags } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeature";
import { useAnalyticsBackState } from "../../projects/hooks/useAnalyticsBack";
import {
  useProjectAnalyticsTabs,
  type ProjectAnalyticsTabs,
} from "./addons/hooks/useAddonsAnalytics";

const ANALYTICS_SEGMENT = "/analytics";

const TAB_ROUTES: Record<string, string> = {
  overview: "",
  viewers: "/viewers",
  document: "/document-highlighter",
  chat: "/ai-chatbot",
  video: "/video-explainer",
  addons: "/addons",
};

const TAB_ICONS: Record<string, React.ReactNode> = {
  overview: <ChartLineIcon size={14} />,
  viewers: <UsersIcon size={14} />,
  document: <FileTextIcon size={14} />,
  chat: <ChatCircleIcon size={14} />,
  video: <PlayIcon size={14} />,
  addons: <SquaresFourIcon size={14} />,
};

const TAB_KEYS = Object.keys(TAB_ROUTES);

// Flag off or no add-on but the video: the video gets a tab of its own.
// Neither while the add-ons are unknown, so the last tab doesn't flip.
const getLastTab = (
  isAddonsEnabled: boolean,
  tabs: ProjectAnalyticsTabs | undefined
) => {
  if (!isAddonsEnabled) return "video";
  if (tabs?.hasAddons) return "addons";
  if (tabs?.hasVideo) return "video";
  return null;
};

export const AnalyticsTabs: React.FC = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const backState = useAnalyticsBackState();
  const isAddonsEnabled = useFeatureFlag(FeatureFlags.AddonsAnalytics);
  const { id: projectId } = useParams<{ id: string }>();
  const tabs = useProjectAnalyticsTabs(projectId, isAddonsEnabled);
  const lastTab = getLastTab(isAddonsEnabled, tabs);

  const basePath = useMemo(() => {
    const idx = location.pathname.indexOf(ANALYTICS_SEGMENT);
    if (idx === -1) {
      return location.pathname;
    }
    return location.pathname.slice(0, idx + ANALYTICS_SEGMENT.length);
  }, [location.pathname]);

  const activeKey = useMemo(() => {
    const suffix = location.pathname.slice(basePath.length);
    const match = TAB_KEYS.find((key) => TAB_ROUTES[key] === suffix);
    return match ?? "overview";
  }, [location.pathname, basePath]);

  const items = useMemo<TabItem[]>(
    () =>
      TAB_KEYS.filter(
        (key) => (key !== "video" && key !== "addons") || key === lastTab
      ).map((key) => ({
        key,
        icon: TAB_ICONS[key],
        label: t(`media.projects.analytics.tabs.${key}`),
      })),
    [t, lastTab]
  );

  const handleChange = (key: string) => {
    if (key === activeKey) {
      return;
    }
    navigate(`${basePath}${TAB_ROUTES[key]}${location.search}`, {
      replace: true,
      state: backState,
    });
  };

  return (
    <Tabs
      variant="line"
      items={items}
      activeKey={activeKey}
      onChange={handleChange}
      tabBarStyle={{ marginBottom: 0, paddingInline: 16 }}
    />
  );
};
